import { CSS2DObject } from "three/examples/jsm/renderers/CSS2DRenderer.js";
import { getLanguage } from "../i18n";
import { effectivePitchIndex, noteNameForPitchIndex, kitPieceIndex } from "../audio";
import { kitLabelsForTimbre } from "../micStore";
import { PlacedObject } from "../objects";
import { AppContext } from "../appContext";

const LABEL_Y_OFFSET = 0;

/**
 * CSS2D note-name labels parented to each pitched collider's mesh: created /
 * destroyed to track the live object list, their text re-derived each frame from
 * the object's pitch index (or kit piece for drums), and shown only when pitch
 * labels are on and the object is audio-enabled. Returns `update` for the render loop.
 */
export function initPitchLabels(ctx: AppContext): { update: () => void } {
  const { field, settings } = ctx;
  const labelMap = new Map<PlacedObject, CSS2DObject>();

  function update(): void {
    const current = field.list();
    const currentSet = new Set<PlacedObject>(current);
    const notation = getLanguage() === "ja" ? "ja" : "en";

    const toDelete: PlacedObject[] = [];
    for (const [obj] of labelMap) {
      if (!currentSet.has(obj)) {
        toDelete.push(obj);
      }
    }
    for (const obj of toDelete) {
      labelMap.get(obj)!.removeFromParent();
      labelMap.delete(obj);
    }

    for (let i = 0; i < current.length; i++) {
      const obj = current[i];
      if (obj.type !== "box" && obj.type !== "sphere" && obj.type !== "funnel") {
        continue;
      }
      if (!labelMap.has(obj)) {
        const div = document.createElement("div");
        div.className = "pitch-label";
        const label = new CSS2DObject(div);
        label.position.set(0, LABEL_Y_OFFSET, 0);
        field.meshAt(i)?.add(label);
        labelMap.set(obj, label);
      } else {
        const label = labelMap.get(obj)!;
        const currentMesh = field.meshAt(i);
        if (currentMesh && label.parent !== currentMesh) {
          currentMesh.add(label);
        }
      }
    }

    for (let i = 0; i < current.length; i++) {
      const obj = current[i];
      const label = labelMap.get(obj);
      if (!label) {
        continue;
      }
      const kitLabels = kitLabelsForTimbre(obj.timbreId);
      label.element.textContent = kitLabels !== null
        ? (kitLabels.length > 0 ? kitLabels[kitPieceIndex(obj.pitchIndex, kitLabels.length)] ?? "?" : "")
        : noteNameForPitchIndex(effectivePitchIndex(obj.pitchIndex, obj.timbreId, field.timbreTransposeFor(obj.timbreId)), true, notation);
      label.visible = settings.showNoteName && !obj.muted && obj.volume > 0;
    }
  }

  return { update };
}
